import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bullseye,
  EmptyState,
  EmptyStateBody,
  Label,
  Pagination,
  SearchInput,
  Spinner,
  Switch,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { CheckCircleIcon } from '@patternfly/react-icons';
import { ADMIN_GPU_LIST_LIMIT, useAdminGpuFindings } from '../../../hooks/useAdminVerdaServers';
import type { VerdaFinding } from '../../../types/models/Verda';
import { formatDateTime } from '../../../utils/dateFormat';
import { findingLink, matchesFindingSearch, severityColor } from './verdaGpuServers';
import { NoSearchResults, useDebouncedValue } from './VerdaGpuParts';
import { VerdaErrorAlert, VerdaRefreshButton } from './VerdaTabParts';

interface VerdaFindingsTabProps {
  /** Defers the fetch until the staff gate has passed. */
  isStaff: boolean;
}

/** The subject cell: linked per the finding rules, plain text otherwise. */
const FindingSubject: React.FunctionComponent<{ finding: VerdaFinding }> = ({ finding }) => {
  const text = finding.subject ?? `${finding.subject_type} ${finding.subject_id}`;
  const to = findingLink(finding);
  return to === null ? <>{text}</> : <Link to={to}>{text}</Link>;
};

/**
 * Admin › Verda › Findings — the usage meter's staff findings, newest first (`GET /admin/verda/findings`,
 * a database read). There is no acknowledge endpoint: the message already says what to do. "Pending
 * only" (`pending=1`) keeps the findings not yet emailed in the digest. Refreshed manually only.
 *
 * List-table decision (Bruno, 2026-09-28): client-side search by stage, subject or message, 400 ms
 * debounce, top + bottom pagination at 25 per page. Client-side filtering is correct because the read
 * is bounded by the backend `limit` (the maximum, 500, is requested).
 */
const VerdaFindingsTab: React.FunctionComponent<VerdaFindingsTabProps> = ({ isStaff }) => {
  const [pendingOnly, setPendingOnly] = useState(false);
  const { findings, loading, error, refetch } = useAdminGpuFindings(pendingOnly, isStaff);

  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebouncedValue(searchValue);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pendingOnly]);

  const filtered = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    return (findings ?? []).filter((finding) => matchesFindingSearch(finding, query));
  }, [findings, debouncedSearch]);

  const paginated = useMemo(() => filtered.slice((page - 1) * perPage, page * perPage), [filtered, page, perPage]);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPage: number) => setPage(newPage);
  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const renderBody = () => {
    if (error) {
      return <VerdaErrorAlert what="GPU findings" message={error} />;
    }
    if (findings === null) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading the GPU findings" />
        </Bullseye>
      );
    }
    if (findings.length === 0) {
      return (
        <EmptyState titleText="No findings" icon={CheckCircleIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            {pendingOnly
              ? 'Every finding has been emailed in the digest.'
              : 'The usage meter has raised no staff finding.'}
          </EmptyStateBody>
        </EmptyState>
      );
    }
    if (filtered.length === 0) {
      return <NoSearchResults what="findings" onClear={() => setSearchValue('')} />;
    }
    return (
      <>
        <Table aria-label="GPU findings" variant="compact">
          <Thead>
            <Tr>
              <Th>Detected</Th>
              <Th>Severity</Th>
              <Th>Stage</Th>
              <Th>Subject</Th>
              <Th>Message</Th>
              <Th>Emailed</Th>
            </Tr>
          </Thead>
          <Tbody>
            {paginated.map((finding) => (
              <Tr key={finding.id}>
                <Td dataLabel="Detected" modifier="nowrap">
                  {formatDateTime(finding.detected_at)}
                </Td>
                <Td dataLabel="Severity">
                  {finding.severity === null ? (
                    '—'
                  ) : (
                    <Label color={severityColor(finding.severity)} isCompact>
                      {finding.severity}
                    </Label>
                  )}
                </Td>
                <Td dataLabel="Stage">
                  <span className="pf-v6-u-font-family-monospace">{finding.stage}</span>
                </Td>
                <Td dataLabel="Subject">
                  <FindingSubject finding={finding} />
                </Td>
                <Td dataLabel="Message">{finding.message ?? '—'}</Td>
                <Td dataLabel="Emailed" modifier="nowrap">
                  {finding.sent_at === null ? (
                    <Label color="orange" isCompact>
                      Pending
                    </Label>
                  ) : (
                    formatDateTime(finding.sent_at)
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        <Pagination
          itemCount={filtered.length}
          perPage={perPage}
          page={page}
          onSetPage={onSetPage}
          onPerPageSelect={onPerPageSelect}
          variant="bottom"
        />
      </>
    );
  };

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <SearchInput
              placeholder="Search by stage, subject or message..."
              value={searchValue}
              onChange={(_event, value) => setSearchValue(value)}
              onClear={() => setSearchValue('')}
            />
          </ToolbarItem>
          <ToolbarItem alignSelf="center">
            <Switch
              id="verda-gpu-findings-pending"
              label="Pending only"
              isChecked={pendingOnly}
              onChange={(_event, checked) => setPendingOnly(checked)}
            />
          </ToolbarItem>
          <ToolbarItem>
            <VerdaRefreshButton loading={loading} onRefresh={refetch} what="GPU findings" />
          </ToolbarItem>
          {findings !== null && !error && (
            <ToolbarItem alignSelf="center">
              <span className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">
                {filtered.length} shown
                {findings.length >= ADMIN_GPU_LIST_LIMIT && ` (the newest ${ADMIN_GPU_LIST_LIMIT} only)`}
              </span>
            </ToolbarItem>
          )}
          {findings !== null && !error && filtered.length > 0 && (
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={filtered.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
                isCompact
              />
            </ToolbarItem>
          )}
        </ToolbarContent>
      </Toolbar>
      {renderBody()}
    </>
  );
};

export default VerdaFindingsTab;
